import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { X, AlertTriangle, Check, ChevronsUpDown } from "lucide-react";
import { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/context/AuthContext';
import { cn } from "@/lib/utils";

const ScheduleStep = ({ stepKey, updateStepData, getStepData }) => {
    const stepData = getStepData(stepKey);
    const configData = getStepData('configuration');
    const actionType = getStepData('actionType');
    const { axiosInstance } = useAuth();
    const [organizationMembers, setOrganizationMembers] = useState([]);
    const [loadingMembers, setLoadingMembers] = useState(false);
    const [recipientsOpen, setRecipientsOpen] = useState(false);
    const hasSetDefaultInterval = useRef(false);

    // Get notification settings from stepData or set defaults
    const notificationSettings = stepData.notification || {
        enabled: false,
        recipients: [],
        notifyOnFailure: true,
        notifyOnRecovery: true,
        cooldownMinutes: 60
    };

    // Set default interval based on action type (only once)
    useEffect(() => {
        if (actionType === 'browser_agent' && !hasSetDefaultInterval.current && stepData.interval === 60) {
            updateStepData(stepKey, { ...stepData, interval: 180 });
            hasSetDefaultInterval.current = true;
        } else if (actionType && actionType !== 'browser_agent') {
            // Mark as set for non-browser_agent types so we don't override later
            hasSetDefaultInterval.current = true;
        }
    }, [actionType]);

    // Fetch organization members when org is selected
    useEffect(() => {
        console.log('configData:', JSON.stringify(configData, null, 2));
        if (configData?.organizationId) {
            fetchOrganizationMembers(configData.organizationId);
        }
    }, [configData?.organizationId]);

    const fetchOrganizationMembers = async (orgId) => {
        setLoadingMembers(true);
        try {
            // Get all organizations, find the one we need
            const orgsResponse = await axiosInstance.get('/organizations');
            const selectedOrg = orgsResponse.data.find(org => org.id === orgId);
            
            if (selectedOrg) {
                // Fetch full organization data with members
                const response = await axiosInstance.get(`/organizations/${selectedOrg.slug}`);
                setOrganizationMembers(response.data.members || []);
            }
        } catch (error) {
            console.error('Failed to fetch members:', error);
        } finally {
            setLoadingMembers(false);
        }
    };

    const handleInputChange = (value) => {
        updateStepData(stepKey, { ...stepData, interval: value });
    };

    const handleNotificationToggle = (enabled) => {
        updateStepData(stepKey, {
            ...stepData,
            notification: {
                ...notificationSettings,
                enabled
            }
        });
    };

    const handleAddRecipient = (userId) => {
        const currentRecipients = notificationSettings.recipients || [];
        if (!currentRecipients.find(r => r.userId === userId)) {
            updateStepData(stepKey, {
                ...stepData,
                notification: {
                    ...notificationSettings,
                    recipients: [...currentRecipients, { userId }]
                }
            });
        }
    };

    const handleRemoveRecipient = (userId) => {
        updateStepData(stepKey, {
            ...stepData,
            notification: {
                ...notificationSettings,
                recipients: notificationSettings.recipients.filter(r => r.userId !== userId)
            }
        });
    };

    const handleNotificationSettingChange = (field, value) => {
        updateStepData(stepKey, {
            ...stepData,
            notification: {
                ...notificationSettings,
                [field]: value
            }
        });
    };

    return (
        <div className="flex flex-col space-y-8 items-center">
            <div className="flex flex-col items-center space-y-3">
                <h1 className="font-semibold text-3xl text-center">Schedule & Alerts</h1>
                <p className="text-center text-muted-foreground">
                    Configure monitoring frequency and notifications
                </p>
            </div>
            
            <div className="w-full max-w-lg mx-auto space-y-6">
                {/* Schedule Section */}
                <div className="space-y-4">
                    <h3 className="font-medium text-lg">Schedule</h3>
                    <div className="flex flex-col space-y-3">
                        <Label htmlFor="interval">Check interval</Label>
                        <Select 
                            onValueChange={handleInputChange} 
                            defaultValue={stepData.interval?.toString() || (actionType === 'browser_agent' ? "180" : "60")}
                        >
                            <SelectTrigger>
                                <SelectValue placeholder="Select interval" />
                            </SelectTrigger>
                            <SelectContent>
                                {actionType === 'browser_agent' ? (
                                    // Browser agent: min 30 minutes, default 3 hours
                                    <>
                                        <SelectItem value="30">Every 30 minutes</SelectItem>
                                        <SelectItem value="60">Every 1 hour</SelectItem>
                                        <SelectItem value="180">Every 3 hours</SelectItem>
                                        <SelectItem value="360">Every 6 hours</SelectItem>
                                        <SelectItem value="720">Every 12 hours</SelectItem>
                                        <SelectItem value="1440">Every 24 hours</SelectItem>
                                    </>
                                ) : (
                                    // HTTP Ping: min 5 minutes, default 1 hour
                                    <>
                                        <SelectItem value="5">Every 5 minutes</SelectItem>
                                        <SelectItem value="15">Every 15 minutes</SelectItem>
                                        <SelectItem value="30">Every 30 minutes</SelectItem>
                                        <SelectItem value="60">Every 1 hour</SelectItem>
                                        <SelectItem value="180">Every 3 hours</SelectItem>
                                        <SelectItem value="360">Every 6 hours</SelectItem>
                                        <SelectItem value="720">Every 12 hours</SelectItem>
                                        <SelectItem value="1440">Every 24 hours</SelectItem>
                                    </>
                                )}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <Separator />

                {/* Notifications Section */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <div className="flex-1">
                            <div className="flex items-center gap-2">
                                <h3 className="font-medium text-lg">Email Notifications</h3>
                                <Badge variant="secondary" className="bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-400 text-[10px] px-1.5 py-0.5">
                                    Beta
                                </Badge>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                Get alerted when your monitor fails or recovers
                            </p>
                        </div>
                        <Switch
                            checked={notificationSettings.enabled}
                            onCheckedChange={handleNotificationToggle}
                        />
                    </div>

                    {notificationSettings.enabled && (
                        <div className="space-y-4 pl-4 border-l-2">
                            {/* Experimental Warning */}
                            <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
                                <div className="flex gap-3">
                                    <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-500 shrink-0 mt-0.5" />
                                    <div className="space-y-1">
                                        <p className="font-medium text-sm text-amber-900 dark:text-amber-400">
                                            Experimental Feature
                                        </p>
                                        <p className="text-xs text-amber-800 dark:text-amber-500">
                                            This feature is currently experimental. You may experience false positives, duplicate notifications, or missed alerts as we continue to refine the system.
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Recipients */}
                            <div className="space-y-2">
                                <Label>Recipients</Label>
                                
                                {/* Selected recipients display */}
                                {notificationSettings.recipients?.length > 0 ? (
                                    <div className="flex flex-wrap gap-1.5 p-2 bg-muted/30 rounded-md border">
                                        {notificationSettings.recipients.map(recipient => {
                                            const member = organizationMembers.find(m => m.id === recipient.userId);
                                            return member ? (
                                                <Badge key={recipient.userId} variant="secondary" className="gap-1">
                                                    {member.name}
                                                    <X 
                                                        className="h-3 w-3 cursor-pointer hover:text-destructive" 
                                                        onClick={() => handleRemoveRecipient(recipient.userId)}
                                                    />
                                                </Badge>
                                            ) : null;
                                        })}
                                    </div>
                                ) : (
                                    <p className="text-xs text-amber-600 dark:text-amber-500 p-2 bg-amber-50 dark:bg-amber-950/30 rounded border border-amber-200 dark:border-amber-800">
                                        At least one recipient is required to enable notifications
                                    </p>
                                )}
                                
                                {/* Combobox */}
                                <Popover open={recipientsOpen} onOpenChange={setRecipientsOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            role="combobox"
                                            aria-expanded={recipientsOpen}
                                            className="w-full justify-between"
                                            disabled={loadingMembers}
                                        >
                                            {loadingMembers ? "Loading members..." : "Add team member..."}
                                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                                        <Command>
                                            <CommandInput placeholder="Search team members..." />
                                            <CommandList>
                                                <CommandEmpty>No team members found.</CommandEmpty>
                                                <CommandGroup>
                                                    {organizationMembers.map(member => {
                                                        const isSelected = notificationSettings.recipients?.some(r => r.userId === member.id);
                                                        return (
                                                            <CommandItem
                                                                key={member.id}
                                                                value={`${member.name} ${member.email}`}
                                                                onSelect={() => {
                                                                    if (isSelected) {
                                                                        handleRemoveRecipient(member.id);
                                                                    } else {
                                                                        handleAddRecipient(member.id);
                                                                    }
                                                                }}
                                                            >
                                                                <Check
                                                                    className={cn(
                                                                        "mr-2 h-4 w-4",
                                                                        isSelected ? "opacity-100" : "opacity-0"
                                                                    )}
                                                                />
                                                                <div className="flex-1">
                                                                    <p className="text-sm font-medium">{member.name}</p>
                                                                    <p className="text-xs text-muted-foreground">{member.email}</p>
                                                                </div>
                                                            </CommandItem>
                                                        );
                                                    })}
                                                </CommandGroup>
                                            </CommandList>
                                        </Command>
                                    </PopoverContent>
                                </Popover>
                                
                                <p className="text-xs text-muted-foreground">
                                    Selected members will receive email notifications for monitor events
                                </p>
                            </div>

                            {/* Notify On */}
                            <div className="space-y-3">
                                <Label>Send notifications for</Label>
                                <div className="space-y-2">
                                    <div className="flex items-center space-x-2">
                                        <Switch
                                            checked={notificationSettings.notifyOnFailure !== false}
                                            onCheckedChange={(v) => handleNotificationSettingChange('notifyOnFailure', v)}
                                        />
                                        <Label className="font-normal">Monitor failures</Label>
                                    </div>
                                    <div className="flex items-center space-x-2">
                                        <Switch
                                            checked={notificationSettings.notifyOnRecovery !== false}
                                            onCheckedChange={(v) => handleNotificationSettingChange('notifyOnRecovery', v)}
                                        />
                                        <Label className="font-normal">Monitor recovery</Label>
                                    </div>
                                </div>
                            </div>

                            {/* Cooldown */}
                            <div className="space-y-2">
                                <Label>Notification cooldown</Label>
                                <Select
                                    value={notificationSettings.cooldownMinutes?.toString() || "60"}
                                    onValueChange={(v) => handleNotificationSettingChange('cooldownMinutes', parseInt(v))}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="30">30 minutes</SelectItem>
                                        <SelectItem value="60">1 hour</SelectItem>
                                        <SelectItem value="120">2 hours</SelectItem>
                                        <SelectItem value="180">3 hours</SelectItem>
                                        <SelectItem value="360">6 hours</SelectItem>
                                        <SelectItem value="720">12 hours</SelectItem>
                                        <SelectItem value="1440">24 hours</SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-muted-foreground">
                                    Minimum time between duplicate notifications
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ScheduleStep;